<template>
    <div class="c2p-container">
        <div class="uv" tabindex="-1">
            <h4 class="uv__main-title" v-text="$ml.get('paymentWithC2P')" tabindex="0" />
            <span class="uv__text" v-text="$ml.get('accessYourSavedCards')" tabindex="0" />

            <div class="uv__input-container">
                <CieloInput
                    :autofocus="false"
                    :borderless="false"
                    name="Email"
                    :label="$ml.get('email')"
                    v-model="email"
                    v-validate="'required|safe-mail'"
                    :data-vv-as="$ml.get('email')"
                    type="email"
                    :error="errors.first('Email')"
                    @change="changeEmail"
                    v-show="emailIsOpen"
                />

                <EditableField
                    :label="$ml.get('email')"
                    :value="this.email"
                    @click-edit="handleEdit('emailIsOpen')"
                    v-show="!emailIsOpen"
                />
            </div>

            <div class="uv__input-container">
                <CieloInput
                    :autofocus="false"
                    :borderless="false"
                    name="Phone"
                    :label="$ml.get('cellPhone')"
                    v-model="phone"
                    v-validate="phoneValidationRule"
                    :data-vv-as="$ml.get('cellPhone')"
                    type="tel"
                    :error="errors.first('Phone')"
                    :mask="phoneMask"
                    v-show="phoneIsOpen"
                />

                <EditableField
                    :label="$ml.get('cellPhone')"
                    :value="this.phone"
                    @click-edit="handleEdit('phoneIsOpen')"
                    v-show="!phoneIsOpen"
                />
            </div>

            <div class="uv__info-container">
                <img src="@/assets/img/c2p-logo-black.svg" />
                <span v-text="$ml.get('howC2PUses')" />
                <InfoTooltip
                    :items="['howC2PUsesTooltipText']"
                    :orientation="renderMobile() ? 'left' : 'bottom'"
                />
            </div>

            <div
                class="uv__card-form"
                v-if="showNewCardForm"
            >
                <NewCardFormC2P
                    :card-number="cardNumber"
                    :card-brand="paymentMethodImage"
                    :holder="holder"
                    :security-code="securityCode"
                    :valid-date="validDate"
                    :installment-list="installmentList"
                    :number-of-installments="numberOfInstallments"
                    @input-number="setCardNumber"
                    @input-holder="(value) => holder = value"
                    @input-security-code="(value) => securityCode = value"
                    @input-date="(value) => validDate = value"
                    @change-installment="(value) => numberOfInstallments = value"
                    :expiry-error="expiryError"
                    :card-error="cardError"
                    :show-mark="false"
                    :disable-installment="!paymentMethodImage || installmentIsFixed"
                />                
                <div class="uv__terms-container">                   
                    <NewCardTermsC2P :brand-def="paymentMethodImage" new-user />  
                </div>

                <div class="uv__remember-container">
                    <CieloCheckBox
                        v-model="rememberMeAgreement"
                        :clickable-slot="true"
                        :text-aria="$ml.get('rememberCards')"
                    >
                        <span class="agreement-text" v-text="$ml.get('rememberCards')" />
                    </CieloCheckBox>

                    <InfoTooltip
                        :items="['rememberCardsTooltipText1', 'rememberCardsTooltipText2']"
                        :orientation="renderMobile() ? 'left' : 'top'"
                    />
                </div>

                <div class="uv__ba-container">
                    <h4 class="uv__title" v-text="$ml.get('billingAddress')" tabindex="0" />

                    <SameShippingAddress v-if="productHasShipping" :same-as-shipping="true" />
        
                    <NationalBillingAddress :address-errors="addressErrors" />
                </div>

                <h4 class="uv__title " v-text="$ml.get('yourPersonalData')" tabindex="0" />

                <div class="uv__pd-container">
                    <CieloInput
                        :autofocus="true"
                        :borderless="false"
                        name="Name"
                        :label="$ml.get('fullName')"
                        v-model="fullName"
                        v-validate="'required|fullname'"
                        :data-vv-as="$ml.get('fullName')"
                        type="text"
                        :error="errors.first('Name')"
                    />

                    <CieloInput
                        :autofocus="false"
                        :borderless="false"
                        name="Identity"
                        :label="$ml.get('identityC2P')"
                        v-model="nationalIdentification"
                        v-validate="isNational ? 'required|numeric|min:11|max:11|cpf-cnpj' : 'required'"
                        :data-vv-as="$ml.get('identityC2P')"
                        :type="isNational ? 'tel' : 'text'"
                        :error="errors.first('Identity')"
                        :mask="isNational ? ['###.###.###-##'] : ''"
                    />
                </div>
                

                <div class="uv__terms-container">
                    <CieloCheckBox
                        v-if="isDofGuest"
                        v-model="agreement"
                        :clickable-slot="true"
                        :text-aria="$ml.get('termsPreffix') + $ml.get('termsSuffix')"
                    >
                        <span v-text="$ml.get('termsPreffix')" />&nbsp;
                        <a target="_blank" :href="showTerms" v-text="$ml.get('termsSuffix')" tabindex="0" />
                    </CieloCheckBox>
                </div>
            </div>

            <LoadingOverlay
                v-if="loadingCardVerifying"
                text="c2pCardVerifying"
            />
            <transition name="fade">
                <CVVRequired
                    v-if="showCVVModal"
                    :security-code="securityCode"
                    @close-modal="closeModal"
                    @submit-cvv="submitCVV"
                    @input-security-code="value => (securityCode = value)"
                />
            </transition>
        </div>

        <ButtonsFinishPayment
            v-if="showNewCardForm"
            :disable-button-next="disableButtonFinish"
            :loading="loading"
            @click-next="finishPayment"
            @click-previous="goToPreviousStep"
        />
        <ButtonsNextPrevious
            v-else
            :disable-button-next="disableButtonNext"
            :loading="loading"
            @click-next="goToNextStep"
            @click-previous="goToPreviousStep"
        />
    </div>
</template>

<script>
    const CieloInput = () => import(
        /* webpackChunkName: "cielo-input" */
        '@/components/template/CieloInput'
    );

    import vars from '@/config/vars';
    import Swal from 'sweetalert2';
    import loader from '@/helpers/loader';
    import SafeMailValidation from '@/helpers/validation-rules/SafeMailValidation';
    import CpfCnpjValidation from '@/helpers/validation-rules/CpfCnpjValidation';
    import FullNameValidation from '@/helpers/validation-rules/FullNameValidation';
    import PaymentProcessor from '@/services/payment/PaymentProcessor';
    import ProductHelper from '@/helpers/productHelper';
    import { isMobile } from '@/helpers/isMobile';
    import { logEvent,logException } from '@/helpers/cieloLogger';
    import { mapGetters } from 'vuex';
    import ButtonsNextPrevious from '@/components/template/ButtonsNextPrevious';
    import InfoTooltip from '@/components/InfoTooltip';
    import EditableField from '@/components/EditableField';
    import NewCardFormC2P from '@/components/ClickToPay/NewCardFormC2P.vue';
    import CieloCheckBox from '@/components/template/CieloCheckBox';
    import LoadingOverlay from '@/components/LoadingOverlay';
    import NewCardTermsC2P from '@/components/ClickToPay/NewCardTermsC2P';
    import ButtonsFinishPayment from '@/components/template/ButtonsFinishPayment';
    import CVVRequired from '@/components/ClickToPay/CVVRequired';
    import NationalBillingAddress from '@/components/ClickToPay/NationalBillingAddress';
    import SameShippingAddress from '@/components/Billing/SameShippingAddress';

    export default {
        components: {
            CieloInput,
            ButtonsNextPrevious,
            InfoTooltip,
            EditableField,
            NewCardFormC2P,
            CieloCheckBox,
            LoadingOverlay,
            NewCardTermsC2P,
            ButtonsFinishPayment,
            CVVRequired,
            NationalBillingAddress,
            SameShippingAddress
        },

        data: () => {
            return {
                isNational: true,
                loading: false,
                loadingCardVerifying: false,
                emailIsOpen: true,
                phoneIsOpen: true,
                showNewCardForm: false,
                expiryError: '',
                cardError: '',
                showTerms: `${vars.CIELO_CHECKOUT_URL}/TransactionalVNext/Content/documents/Termos%20e%20condicoes%20de%20Uso_Cielo.pdf`,
                // agreement: false,
                showCVVModal: false,
                addressErrors: {},
            };
        },

        watch: {
            showNewCardForm(value) {
                const canCloseEmail = !this.errors.items.some(item => item.field === 'Email');
                const canClosePhone = !this.errors.items.some(item => item.field === 'Phone');
                
                if (value) {
                    this.$validator.extend('cpf-cnpj', CpfCnpjValidation);
                    this.$validator.extend('fullname', FullNameValidation);
                }

                if(canCloseEmail && this.email) {
                    this.emailIsOpen = false;
                }

                if(canClosePhone & this.phone) {
                    this.phoneIsOpen = false;
                }
            },
        },

        mounted() {
            if (this.cardNumber) {
                this.doBinQuery();
            }

            loader.multiLanguage = this.$ml;

            if (this.securityCode || this.validDate || this.holder || this.cardNumber) {
                this.$validator.validate();
                this.cancel();
            }

            this.$validator.extend('safe-mail', SafeMailValidation);

            if(this.email) {
                this.emailIsOpen = false;
                this.setShowNewCardForm();
            }

            if(this.phone) {
                this.phoneIsOpen = false;
            }
        },

        computed: {
            ...mapGetters({
                selectedPaymentMethod: 'payment/getSelectedPaymentMethod',
                getInstallmentListForPaymentMethodSelected: 'payment/getInstallmentListForPaymentMethodSelected',
                sameAsShippingAddress: 'billingAddress/getSameAsShippingAddress',
                installmentIsFixed: 'payment/getSelectedPaymentMethodInstallmentIsAbleToBeFixed',
                billingAddress: 'billingAddress/getBillingAddress',
                isDofGuest: 'dataOnFile/isDofGuest',
            }),

            email: {
                get: self => self.$store.state.clickToPayPayment.consumer.email,
                set(value) { this.$store.state.clickToPayPayment.consumer.email = value; }
            },
            phone: {
                get: self => self.$store.state.clickToPayPayment.consumer.phone,
                set(value) { this.$store.state.clickToPayPayment.consumer.phone = value; }
            },
            securityCode: {
                get: self => self.$store.state.cardPayment.SecurityCode,
                set(value) { this.$store.commit('cardPayment/setSecurityCode', value); }
            },
            cardNumber: {
                get: self => self.$store.state.cardPayment.CardNumber || '',
                set(value) { this.$store.commit('cardPayment/setCardNumber', value); }
            },
            holder: {
                get: self => self.$store.state.cardPayment.Holder,
                set(value) { this.$store.commit('cardPayment/setHolder', value); }
            },
            numberOfInstallments: {
                get: self => self.$store.getters['payment/getInstallmentNumberOfPayments'],
                set(value) { this.$store.commit('payment/setNumberOfInstallments', value); }
            },
            rememberMeAgreement: {
                get: self => self.$store.state.clickToPayPayment.rememberMeAgreement,
                set(value) { this.$store.state.clickToPayPayment.rememberMeAgreement = value; }
            },
            validDate: {
                get() {
                    let month = this.$store.state.cardPayment.Expiration.Month;
                    let year = this.$store.state.cardPayment.Expiration.Year;

                    return `${month}${year}`;
                },
                set(value) {
                    const values = value.split('');
                    const month = `${values[0]}${values[1]}`;
                    const year = `${values[2]}${values[3]}`;
                    let expirationMonth = (month >= 0) ? month : '';
                    let expirationYear = (year >= 2) ? year : '';

                    let today = new Date();
                    let cardExpiration = new Date(`20${year}`, month);

                    if (month < 1 || month > 12) {
                        this.expiryError = this.$ml.get('dateMonthCardError');
                    }
                    else if (cardExpiration < today) {
                        this.expiryError = this.$ml.get('cardExpirationError');
                    } else {
                        this.expiryError = '';
                    }

                    this.$store.commit('cardPayment/setExpirationMonth', expirationMonth);
                    this.$store.commit('cardPayment/setExpirationYear', expirationYear);
                }
            },
            fullName: {
                get: self => self.$store.state.clickToPayPayment.consumer.fullName,
                set(value) { this.$store.state.clickToPayPayment.consumer.fullName = value; }
            },
            nationalIdentification: {
                get: self => self.$store.state.clickToPayPayment.consumer.nationalIdentification,
                set(value) { this.$store.state.clickToPayPayment.consumer.nationalIdentification = value; }
            },
            agreement: {
				get: (self) => self.$store.state.customer.Agreement,
				set(value) {
					this.$store.commit('customer/setAgreement', value);
				},
			},

            consumerPresent() {
                return this.$store.state.clickToPayPayment.consumerPresent;
            },

            hasCards() {
                const cards = this.$store.state.clickToPayPayment.cards;
                return cards !== null && cards.length > 0;
            },

            disableButtonNext() {
                return !this.email || !this.phone || this.errors.items.length > 0;
            },

            isBillingFormInvalid() {
                if(this.sameAsShippingAddress) return false;

                const address = this.billingAddress;
                
                if (this.isNational) {
                    return (
                        !address.Street ||
                        !address.State ||
                        !address.City ||
                        !address.Number ||
                        !address.District ||
                        !address.ZipCode
                    );
                } else {
                    return (
                        !address.Street ||
                        !address.State ||
                        !address.City ||
                        !address.ZipCode
                    );
                }
            },

            disableButtonFinish() {
                const hasErrors = this.errors.items.length > 0;
                const missingCommonFields = !this.email || !this.phone || !this.nationalIdentification || !this.agreement || !this.numberOfInstallments;
                const missingAddressFields = this.isBillingFormInvalid;
                const missingFullName = !this.fullName;
                
                
                return missingFullName || missingCommonFields || missingAddressFields || hasErrors;
            },

            installmentList() {
                return this.selectedPaymentMethod ? this.getInstallmentListForPaymentMethodSelected : [];
            },

            paymentMethodImage() {
                return this.selectedPaymentMethod ? this.selectedPaymentMethod.Image : null;
            },

            productHasShipping: () => ProductHelper.productHasShipping(),

            phoneValidationRule() {
				return 'required|numeric|min:11';
			},
			phoneMask() {
				return '(##) #####-####';
			},
        },

        methods: {
            async changeEmail(email) {
                if (this.hasCards) {
                    return;
                }

                this.loadingCardVerifying = true;

                try {                    
                    await this.$store.dispatch('clickToPayPayment/idLookup', email);

                    this.loadingCardVerifying = false;

                    this.setShowNewCardForm();
                } catch (error) {

                    this.loadingCardVerifying = false;

                    logException(error);
                }
            },

            async goToNextStep() {
                this.loading = true;

                if (this.hasCards) {
                    this.$store.dispatch('pipeline/pushTo', 'CardListC2P');
                    return;
                }

                if (this.consumerPresent) {
                    try {
                        await this.$store.dispatch('clickToPayPayment/initiateValidation');
                        this.loading = false;

                        logEvent({
                            category: 'click_to_pay',
                            action: 'dispositivo_nao_reconhecido_c2p',
                            label: 'Usuário preenche dados de e-mail e telefone e clica em continuar para seguir com a autenticação OTP'
                        });

                        this.$store.dispatch('pipeline/pushTo', 'IdLookUpAuthenticationC2P');
                    } catch (error) {
                        Swal.fire({
                            type: 'error',
                            title: this.$ml.get('errorOTPHeading'),
                            text: this.$ml.get('errorOTPBody'),
                            confirmButtonText: this.$ml.get('errorOTPCTA'),
                            allowOutsideClick: false,
                            allowEscapeKey: false
                        }).then(() => {
                            this.goToPreviousStep();
                        });

                        this.loading = false;
                        logException(error);
                    }

                    return;
                }
            },

            async finishPayment() {
                if(!this.loading) {
                    this.loading = true;
                    const popup = window.open('', 'mc_c2p', 'width=600,height=600');
                    if(!popup) {
                        this.showErrorMessage('Habilite pop-ups para finalizar seu pagamento');
                        this.loading = false;
                        return;
                    }

                    await this.$store.dispatch('clickToPayPayment/checkoutWithNewCard', { windowRef: popup });
                    const responseCode = this.$store.state.clickToPayPayment.checkoutResponse.actionCode;
                   
                    try {
                        popup.close();
                    } catch (error) {
                        logException(error);
                    }
                   
                    if (responseCode !== 'COMPLETE') {
                        this.$store.dispatch('pipeline/pushTo', 'ChangePaymentType');
                        this.loading = false;
                    } else {
                        try {
                            await PaymentProcessor.Process();
                            this.loading = false;

                            this.finishGAEvent();
                        } catch (error) {
                            if (error.message === 'cvvRequiredError') {
                                this.showCVVModal = true;
                            }
                            logException(error);
                            this.loading = false;
                        }
                    }
                }
            },

            goToPreviousStep() {
                this.cancel();
                this.$store.dispatch('pipeline/pushTo', 'ChangePaymentType');
            },

            handleEdit(field) {
                this[field] = true;
                
                if(field === 'emailIsOpen') {
                    this.email = '';
                    this.showNewCardForm = false;
                }

                if(field === 'phoneIsOpen') {
                    this.phone = '';
                }
            },

            renderMobile() {
                return isMobile();
            },

            cancel() {
                this.$store.commit('payment/setNumberOfInstallments', 0);
            },

            reset() {
                this.$store.state.clickToPayPayment.consumerPresent = false;
                this.$store.state.clickToPayPayment.otpValidation.error = '';
                this.$store.state.clickToPayPayment.consumer.email = '';
            },

            setCardNumber(value) {
                const oldBin = this.cardNumber.substring(0, 8);
                const newBin = value.substring(0, 8);
                this.cardNumber = value;

                if (value && value.length >= 8 && (newBin !== oldBin)) {
                    this.doBinQuery();
                }
            },

            async doBinQuery() {
                try {
                    let cardBin = this.$store.getters['cardPayment/getCardBin'];
                    await this.$store.dispatch('clickToPayPayment/fetchCardBrand', cardBin);
                    return true;
                }
                catch (error) {
                    logEvent({
                        category: 'BinQuery',
                        action: 'Do Bin Query',
                        label: `[BIN QUERY ERROR]: ${error}`
                    });

                    const cardBrandError = this.$store.state.clickToPayPayment.cardBrandError;

                    await Swal.fire({
                        type: 'error',
                        title: 'Oppsss!',
                        text: cardBrandError ?
                            this.$ml.get(cardBrandError) :
                            this.$ml.get('processingRequestError'),
                        confirmButtonText: this.$ml.get('retry'),
                        cancelButtonText: this.$ml.get('cancel'),
                        showCancelButton: cardBrandError ? false : true,
                        showLoaderOnConfirm: cardBrandError ? false : true,
                        preConfirm: cardBrandError ? null : this.doBinQuery,
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    });
                    this.cardError = this.$ml.get('numberCardError');

                    this.cardNumber = '';
                    this.$store.state.payment.PaymentMethodId = null;
                    return false;
                }
            },

            isCardExpirationValid() {
                let month = this.$store.state.cardPayment.Expiration.Month || 0;
                let year = this.$store.state.cardPayment.Expiration.Year || 0;

                if (month === 0) return false;
                if (month > 12) return false;

                let today = new Date();
                let cardExpiration = new Date(`20${year}`, month);

                return cardExpiration >= today;
            },

            setShowNewCardForm() {
                this.showNewCardForm = !this.hasCards && !this.consumerPresent;
            },

            closeModal() {
                this.showCVVModal = false;
            },

            async submitCVV() {
                if(!this.loading) {
                    this.showCVVModal = false;
                    this.loading = true;
                    await PaymentProcessor.Process();
                    this.loading = false;

                    this.finishGAEvent();
                }
            },

            finishGAEvent() {
                logEvent({
                    category: 'click_to_pay',
                    action: 'finalizacao_novo_usuario_c2p',
                    label: 'Usuário preenche informações de contato, dados pessoais, dados de cartão e clica em CONCLUIR COMPRA'
                });
            },
        },
    };
</script>

<style lang="scss" scoped>
    .uv {
        margin-bottom: 24px;
        
        &__main-title {
            display: block;
            font-size: 16px;
            font-weight: 700;
            margin-bottom: 8px;
            color: #2F363E;
        }

        &__title {
            display: block;
            font-size: 16px;
            font-weight: 700;
            margin-bottom: 16px;
            color: #2F363E;
        }

        &__text {
            display: block;
            font-size: 14px;
            font-weight: 500;
            color: #5C6A7B;
            margin-bottom: 20px;
        }

        &__input-container {
            width: 100%;
            .ef {
                margin-bottom: 20px;
            }
        }

        &__info-container {
            position: relative;
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 16px;
            border-radius: 12px;
            background-color: #EFF1F3;

            &--terms {
                display: block;
                margin-top: 32px;
            }

            img {}

            span {
                font-size: 14px;
                font-weight: 500;
                color: #000000;
            }

            a {
                color: #0667CD;
                font-weight: 700;
                text-decoration: underline;
            }

        }

        &__card-form {
            margin-top: 32px;
        }

        &__remember-container {
            display: flex;
            align-items: center;
            gap: 16px;
            margin: 20px 0 32px;
        }

        &__ba-container {
            margin-bottom: 32px;
            
            @media screen and (min-width: 1025px) {
                margin-bottom: 16px;
            }
        }

        &__pd-container {
            margin-bottom: 32px;

            @media screen and (min-width: 1025px) {
                display: flex;
                gap: 16px;
                margin-bottom: 12px;
            }

            .cielo-input {
                @media screen and (min-width: 1025px) {
                    width: 100%;
                }
            }
        }

        &__terms-container {}
    }

    .nct {
        margin-top: 20px;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity .3s;
    }

    /* .fade-leave-active em versões anteriores a 2.1.8 */
    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }
</style>
